import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, MenuItem } from '@mui/material';
import { apiRequest } from 'api-client';
import apiEndpoints from 'constants/endpoints';
import { KozoomStream } from 'constants/types';
import moment from 'moment';
import { DataGridListPage } from 'pages/Common';
import { DataGridActionMenu } from 'pages/Common/DataGridActionMenu';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteDataGridRow, getDataGridList, saveRowRankings } from 'store/actions';
import { pageKeys } from 'store/reducers/dataGrid.reducer';
import { getEventName } from 'utils/event';
import { StreamAddEdit } from './components/StreamAddEdit';


export const EventStreams = () => {
  const gamesById = useAppSelector(state => state.games.gamesById);
  const [ eventDetail, setEventDetail ] = useState({});
  const [ stream, setStream ] = useState<KozoomStream | undefined>();
  const [ dialogOpen, setDialogOpen ] = useState(false);
  const [ deletingAll, setDeletingAll] = useState(false);
  const { eventId } = useParams<{eventId: string}>();
  const dispatch = useAppDispatch();

  const onDelete = useCallback((streamId: number) => {
    const confirmed = window.confirm("Are you sure you want to remove this permanently?");
    if (confirmed) {
      dispatch(deleteDataGridRow(apiEndpoints.streams, streamId, {}, {}, pageKeys.eventStreams));
    }
  }, [dispatch]);

  const onEdit = useCallback((stream: KozoomStream) => {
    setStream(stream);
    setDialogOpen(true);
  }, []);

  const onAdd = () => {
    setStream(undefined);
    setDialogOpen(true);
  }

  const onSaveRankings = (ids: number[]) => {
    dispatch(saveRowRankings(apiEndpoints.streams, {ids}, {}, pageKeys.eventStreams));
  }

  const onDeleteAll = () => {
    const confirmed = window.confirm("Are you sure you want to remove all streams permanently?");
    if (confirmed) {
      setDeletingAll(true);
      apiRequest.delete(`/${apiEndpoints.streams}`, {
        params: {
          eventId
        }
      }).then(data => {
        toast.success('All streams deleted successfully');
        
        dispatch(getDataGridList(`${apiEndpoints.streams}`, {eventId}, pageKeys.eventStreams));
      }).catch(err => {
        toast.error(err.message);
      }).finally(() => {
        setDeletingAll(false);
      });
    }
  }

  const onClose = () => {
    setDialogOpen(false);
  }

  useEffect(() => {
    apiRequest.get(`/${apiEndpoints.events}/${eventId}`).then(data => {
      setEventDetail(data);
    }).catch(err => {
      setEventDetail({});
      toast.error(err.message);
    })
  }, [eventId]);

  const columns = useMemo(() => {
    return [
      {
        field: 'rank',
        headerName: 'Rank',
        width: 80,
        rowDrag: true
      },
      {
        field: 'label',
        headerName: 'Table Label',
        width: 150,
      },
      {
        field: 'level',
        headerName: 'Level',
        width: 150,
      },
      {
        field: 'token',
        headerName: 'Token',
        width: 150,
      },
      {
        field: 'delay',
        headerName: 'Delay',
        width: 150,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        valueFormatter: ({ value }: {value: string}) => value && moment(value).format('Y-MM-DD'),
        width: 120,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        cellStyle: {textAlign: 'center'},
        headerClass: 'text-center',
        cellRenderer: ({ data }: {data: KozoomStream}) => (
          <DataGridActionMenu>
            <MenuItem onClick={() => onEdit(data)}><EditIcon/>Edit</MenuItem>
            <MenuItem onClick={() => onDelete(data.id)}><DeleteForeverIcon/>Delete</MenuItem>
          </DataGridActionMenu>
        ),
      },
    ];
  }, [onDelete, onEdit]);

  return (
    <>
      <DataGridListPage
        columns={columns as any}
        apiEndpoint={apiEndpoints.streams}
        heading={`Streams - ${getEventName(eventDetail, gamesById)}`}
        pageKey={pageKeys.eventStreams}
        onAdd={onAdd}
        onSaveRankings={onSaveRankings}
        rowsPerPage={50}
        initialQueryParams={{
          eventId
        }}
        gridOptions={undefined}
        customHeadingToolbar={<Box mb={2}>
          <LoadingButton
            loading={deletingAll}
            variant="contained"
            color="error"
            onClick={onDeleteAll}
            startIcon={<DeleteForeverIcon />}
          >
            Delete all
          </LoadingButton>
        </Box>}
        />
      <StreamAddEdit
        stream={stream}
        eventId={Number(eventId)}
        open={dialogOpen}
        onClose={onClose}
      />
    </>
  )
}

